import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box, Button, Chip, Stack, Typography } from '@mui/material'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckIcon from '@mui/icons-material/Check'

import GetStartedLayout from './GetStartedLayout'
import { PLAN_CARDS } from './planCards'
import { GS, getStartedPrimaryBtnSx } from './getStartedTheme'
import { loadRegistration } from '../../utils/onboardingStorage'
import { loadControlPlaneSession } from '../../api/onboardingApi'
import { openHubSpotLink } from '../../utils/hubspot'
import { LicensePlan } from '../../types/license'
import { OwnerRegistration } from '../../types/onboardingRegistration'

const WelcomeLanding: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [registration, setRegistration] = useState<OwnerRegistration | null>(
    null
  )

  useEffect(() => {
    const session = loadControlPlaneSession()
    const stored = loadRegistration()
    if (!session?.owner?.emailVerified && !stored?.emailVerified) {
      navigate('/get-started', { replace: true })
      return
    }
    setRegistration(
      stored || {
        companyName: '',
        companyAddress: '',
        firstName: session?.owner.firstName || '',
        lastName: session?.owner.lastName || '',
        username: '',
        password: '',
        phone: '',
        jobTitle: '',
        need: '',
        country: '',
        professionalEmail: session?.owner.email || '',
      }
    )
  }, [navigate])

  const handlePlanClick = async (plan: LicensePlan) => {
    await openHubSpotLink({
      source: 'plan-select',
      plan,
      registration: registration ?? undefined,
    })
  }

  const handleRequestDemo = async () => {
    await openHubSpotLink({
      source: 'request-demo',
      registration: registration ?? undefined,
    })
  }

  if (!registration) return null

  return (
    <GetStartedLayout
      step={3}
      wide
      panel={false}
      title={t('getStarted.welcome.title', 'Welcome to INCYIA')}
      subtitle={t(
        'getStarted.welcome.subtitle',
        'Your account is verified. Choose a pack, watch a demo, or book a walkthrough with our team.'
      )}
    >
      <Stack spacing={3.5}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            p: { xs: 2, sm: 2.25 },
            borderRadius: '14px',
            bgcolor: GS.tealSoft,
            border: `1.5px solid #9DD9CE`,
            boxShadow: '0 10px 28px -18px rgba(7,107,92,.45)',
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontFamily: GS.font,
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: GS.tealDeep,
                mb: 0.5,
              }}
            >
              {t('getStarted.welcome.demoBannerEyebrow', 'Recommended next step')}
            </Typography>
            <Typography
              sx={{
                fontFamily: GS.font,
                fontSize: 17,
                fontWeight: 700,
                color: GS.text,
                lineHeight: 1.3,
                mb: 0.35,
              }}
            >
              {t(
                'getStarted.welcome.demoBannerTitle',
                'Book a personalized walkthrough'
              )}
            </Typography>
            <Typography
              sx={{
                fontFamily: GS.font,
                fontSize: 14,
                color: GS.muted,
                lineHeight: 1.45,
                maxWidth: '46ch',
              }}
            >
              {t(
                'getStarted.welcome.demoBannerBody',
                'Talk with our team about your programme — packs, onboarding, and what fits your context.'
              )}
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="large"
            startIcon={<EventAvailableOutlinedIcon />}
            endIcon={<OpenInNewIcon />}
            onClick={handleRequestDemo}
            sx={{
              ...getStartedPrimaryBtnSx,
              flexShrink: 0,
              alignSelf: { xs: 'stretch', sm: 'center' },
              px: 2.75,
              py: 1.35,
              bgcolor: GS.tealDeep,
              boxShadow: '0 2px 8px rgba(7,107,92,0.35)',
              '&:hover': { bgcolor: '#055548' },
            }}
          >
            {t('getStarted.welcome.requestDemo', 'Request a demo')}
          </Button>
        </Box>

        <Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'baseline' }}
            justifyContent="space-between"
            spacing={1}
            mb={2}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: GS.font,
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: GS.muted,
                  mb: 0.5,
                }}
              >
                {t('getStarted.welcome.plansTitle', 'Available packs')}
              </Typography>
              <Typography
                sx={{
                  fontFamily: GS.font,
                  fontSize: 15,
                  color: GS.muted,
                  lineHeight: 1.5,
                  maxWidth: '52ch',
                }}
              >
                {t(
                  'getStarted.welcome.plansHint',
                  'Three packs — pick the fit for your programme. Our team will follow up.'
                )}
              </Typography>
            </Box>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(3, minmax(0, 1fr))',
              },
              gap: 1.75,
              alignItems: 'stretch',
            }}
          >
            {PLAN_CARDS.map((plan) => (
              <Box
                key={plan.key}
                component="button"
                type="button"
                onClick={() => handlePlanClick(plan.key)}
                sx={{
                  position: 'relative',
                  width: '100%',
                  textAlign: 'left',
                  p: 0,
                  m: 0,
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  fontFamily: GS.font,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    p: 2.5,
                    pt: plan.featured ? 3.25 : 2.5,
                    borderRadius: '14px',
                    border: `1.5px solid ${
                      plan.featured ? plan.color : plan.border
                    }`,
                    bgcolor: '#fff',
                    boxShadow: plan.featured
                      ? '0 16px 36px -22px rgba(0,20,109,.45)'
                      : '0 8px 24px -18px rgba(0,20,109,.28)',
                    transition:
                      'border-color 0.15s, transform 0.15s, box-shadow 0.15s',
                    '&:hover': {
                      borderColor: plan.color,
                      transform: 'translateY(-3px)',
                      boxShadow: '0 18px 32px -18px rgba(6,21,36,.38)',
                    },
                  }}
                >
                  {plan.featured && (
                    <Chip
                      label={t(
                        'getStarted.welcome.popular',
                        'Most popular'
                      )}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 10,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        height: 22,
                        fontFamily: GS.font,
                        fontWeight: 700,
                        fontSize: 11,
                        letterSpacing: '0.04em',
                        bgcolor: plan.color,
                        color: '#fff',
                        '& .MuiChip-label': { px: 1.25 },
                      }}
                    />
                  )}

                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      bgcolor: plan.bg,
                      color: plan.color,
                      display: 'grid',
                      placeItems: 'center',
                      mb: 1.5,
                    }}
                  >
                    {plan.icon}
                  </Box>

                  <Typography
                    sx={{
                      fontFamily: GS.font,
                      fontWeight: 700,
                      fontSize: 18,
                      color: GS.text,
                      mb: 0.35,
                    }}
                  >
                    {t(`license.packs.${plan.pack}`, plan.name)}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: GS.font,
                      fontSize: 13,
                      color: GS.muted,
                      lineHeight: 1.45,
                      mb: 1.75,
                      minHeight: 38,
                    }}
                  >
                    {t(`license.packTaglines.${plan.pack}`)}
                  </Typography>

                  <Box
                    component="ul"
                    sx={{
                      m: 0,
                      mb: 2,
                      p: 0,
                      listStyle: 'none',
                      flex: 1,
                    }}
                  >
                    {plan.highlights.map((h) => (
                      <Box
                        key={h}
                        component="li"
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <CheckIcon
                          sx={{
                            fontSize: 16,
                            color: plan.color,
                            mt: '2px',
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          sx={{
                            fontFamily: GS.font,
                            fontSize: 13.5,
                            color: GS.muted,
                            lineHeight: 1.4,
                          }}
                        >
                          {t(`license.packFeatures.${h}`)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  <Typography
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      fontFamily: GS.font,
                      fontSize: 14,
                      fontWeight: 700,
                      color: plan.color,
                      mt: 'auto',
                    }}
                  >
                    {t('getStarted.welcome.choosePlan', 'Get in touch')}
                    <ArrowForwardIcon sx={{ fontSize: 16 }} />
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' },
            gap: 1.75,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: GS.font,
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: GS.muted,
                mb: 1,
              }}
            >
              {t('getStarted.welcome.demoTitle', 'Product demo')}
            </Typography>
            <Box
              sx={{
                borderRadius: '12px',
                minHeight: 148,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: GS.navy,
                border: `1px solid ${GS.line}`,
                backgroundImage: `
                  radial-gradient(ellipse at 30% 20%, rgba(13,155,135,0.28), transparent 55%),
                  radial-gradient(ellipse at 80% 80%, rgba(255,255,255,0.06), transparent 45%)
                `,
              }}
            >
              <Stack alignItems="center" spacing={0.75}>
                <PlayCircleOutlineIcon
                  sx={{ fontSize: 42, color: 'rgba(255,255,255,0.55)' }}
                />
                <Typography
                  sx={{
                    fontFamily: GS.font,
                    fontSize: 13.5,
                    color: 'rgba(255,255,255,0.55)',
                  }}
                >
                  {t(
                    'getStarted.welcome.demoPlaceholder',
                    'Demo video coming soon'
                  )}
                </Typography>
              </Stack>
            </Box>
          </Box>

          <Box>
            <Typography
              sx={{
                fontFamily: GS.font,
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: GS.muted,
                mb: 1,
              }}
            >
              {t('getStarted.welcome.docsTitle', 'Documentation')}
            </Typography>
            <Box
              sx={{
                p: 2.5,
                borderRadius: '12px',
                minHeight: 148,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1px solid ${GS.line}`,
                bgcolor: '#fff',
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  bgcolor: GS.tealSoft,
                  display: 'grid',
                  placeItems: 'center',
                  mb: 1.25,
                }}
              >
                <MenuBookOutlinedIcon
                  sx={{ fontSize: 22, color: GS.tealDeep }}
                />
              </Box>
              <Typography
                sx={{
                  fontFamily: GS.font,
                  fontSize: 13.5,
                  color: GS.muted,
                  textAlign: 'center',
                  lineHeight: 1.5,
                  maxWidth: '28ch',
                }}
              >
                {t(
                  'getStarted.welcome.docsPlaceholder',
                  'Product documentation will be available here soon.'
                )}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            textAlign: 'center',
            pt: 0.5,
            borderTop: `1px solid ${GS.lineSoft}`,
          }}
        >
          <Button
            variant="contained"
            size="large"
            startIcon={<EventAvailableOutlinedIcon />}
            endIcon={<OpenInNewIcon />}
            onClick={handleRequestDemo}
            sx={{ ...getStartedPrimaryBtnSx, px: 3.5, py: 1.35, mt: 2 }}
          >
            {t('getStarted.welcome.requestDemo', 'Request a demo')}
          </Button>
          <Typography
            sx={{
              display: 'block',
              mt: 1.25,
              fontFamily: GS.font,
              fontSize: 13.5,
              color: GS.muted2,
            }}
          >
            {t(
              'getStarted.welcome.hubspotHint',
              'You will be redirected to our scheduling page to book a session with our team.'
            )}
          </Typography>
        </Box>
      </Stack>
    </GetStartedLayout>
  )
}

export default WelcomeLanding
